import { AlertTriangle, ArrowLeft } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Skeleton,
} from '@xanectech/ui/components';
import { useNavigate } from 'react-router-dom';
import { useAboutByAppName } from '@xanectech/data';
import { useTheme } from './ThemeProvider';
import { createLogger } from '@xanectech/lib';
import { useEffect } from 'react';
import { cn } from '@xanectech/ui/lib';

const logger = createLogger('Admin - About Component');

interface AboutProps {
  description: string;
  rounded?: boolean;
}

export const About: React.FC<AboutProps> = ({
  description,
  rounded = true,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { data: about, isLoading, error } = useAboutByAppName('Admin');

  if (error) {
    return (
      <div
        className={cn('container mx-auto px-4 py-8', {
          'rounded-none': !rounded,
        })}
      >
        <div className='max-w-4xl mx-auto'>
          <Card className={cn({ 'rounded-none': !rounded })}>
            <CardContent className='pt-6'>
              <div className='text-center'>
                <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
                <h3 className='text-lg font-medium mb-2'>
                  Error Loading About Information
                </h3>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  return (
    <>
      {isLoading ? (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          <div className='lg:col-span-2 space-y-6'>
            <Card>
              <CardHeader>
                <Skeleton className='h-6 w-48' />
                <Skeleton className='h-4 w-64' />
              </CardHeader>
              <CardContent className='space-y-4'>
                <Skeleton className='h-20 w-full' />
                <Skeleton className='h-4 w-full' />
                <Skeleton className='h-4 w-3/4' />
              </CardContent>
            </Card>
          </div>
          <div className='space-y-6'>
            <Card>
              <CardHeader>
                <Skeleton className='h-6 w-32' />
              </CardHeader>
              <CardContent>
                <Skeleton className='h-32 w-full' />
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className={cn('space-y-6', { 'rounded-none': !rounded })}>
          <Card className={cn({ 'rounded-none': !rounded })}>
            <CardHeader>
              <CardTitle>About</CardTitle>
              <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Version:</span>
                  <span>{about?.version || 'N/A'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Last Updated:</span>
                  <span>
                    {about?.release_date
                      ? new Date(
                          about.release_date as string,
                        ).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Release Notes:</span>
                  <span>
                    {about?.release_notes_link &&
                    about?.release_notes_link !== '' ? (
                      <a
                        href={about.release_notes_link}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        View Release Notes
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </span>
                </div>
              </div>
              <div className='space-y-2 text-sm mt-10 h-20 border-t-4 border-primary'>
                <div className='flex flex-col justify-between items-center h-full pt-3'>
                  <h3 className='font-bold text-primary'>Terms of Service</h3>
                  <p className='text-muted-foreground'>
                    <a
                      href='https://xanectech.com/terms-of-service'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {about?.terms_of_service ||
                        'No terms of service provided.'}
                    </a>
                  </p>
                </div>
              </div>
              <div className='space-y-2 text-sm mt-10 h-20 border-t-4 border-primary'>
                <div className='flex justify-between items-center h-full'>
                  <span className='text-muted-foreground'>Theme:</span>
                  <span className='capitalize'>{theme}</span>
                </div>
              </div>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-end py-4 border-t-4 border-primary'>
                  <span className=''>{about?.copyright || ''}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};
